import {
  WorkshopWithMetrics,
  CreateWorkshopData,
  UpdateWorkshopData,
  WorkshopsParams,
  EnrollmentWithUser,
  WorkshopPayment,
  WorkshopAlert,
} from "../../models/workshops";
import { api } from "./api";

const workshopTag = (id: string | number) => ({
  type: "workshops" as const,
  id: String(id),
});

export const workshopsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ─── Workshop CRUD ────────────────────────────────────────────

    getWorkshops: build.query<
      { data: WorkshopWithMetrics[]; total: number },
      WorkshopsParams
    >({
      query: (params) => ({
        url: "/bo/workshops",
        params,
      }),
      transformResponse: (response: any) => {
        // Backend returns paginated format: { success, data: { items, pagination } }
        if (response.success && response.data?.items) {
          return {
            data: response.data.items,
            total: response.data.pagination?.total ?? response.data.items.length,
          };
        }
        const data = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return {
          data,
          total: response.total ?? data.length,
        };
      },
      providesTags: ["workshops"],
    }),

    getWorkshopById: build.query<WorkshopWithMetrics, string>({
      query: (id) => ({
        url: `/bo/workshops/${id}`,
      }),
      transformResponse: (response: any) => {
        return response.success && response.data ? response.data : response;
      },
      providesTags: (result, error, id) => [workshopTag(id)],
    }),

    createWorkshop: build.mutation<WorkshopWithMetrics, CreateWorkshopData>({
      query: (data) => ({
        url: "/bo/workshops",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["workshops"],
    }),

    updateWorkshop: build.mutation<void, UpdateWorkshopData>({
      query: ({ id, ...data }) => ({
        url: `/bo/workshops/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        workshopTag(id),
        "workshops",
      ],
    }),

    deleteWorkshop: build.mutation<void, string>({
      query: (id) => ({
        url: `/bo/workshops/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        workshopTag(id),
        "workshops",
      ],
    }),

    updateWorkshopsSortOrder: build.mutation<
      void,
      { tenantId: string; workshops: { id: string; sortOrder: number }[] }
    >({
      query: (body) => ({
        url: "/bo/workshops/sort-order",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["workshops"],
    }),

    // ─── Enrollments ──────────────────────────────────────────────

    getWorkshopEnrollments: build.query<EnrollmentWithUser[], string>({
      query: (workshopId) => ({
        url: `/bo/workshops/${workshopId}/enrollments`,
      }),
      transformResponse: (response: any) => {
        return response.success && Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
      },
      providesTags: (result, error, workshopId) => [
        { type: "workshops", id: `${workshopId}-enrollments` },
      ],
    }),

    markAttendance: build.mutation<
      void,
      { workshopId: string; enrollmentId: string }
    >({
      query: ({ workshopId, enrollmentId }) => ({
        url: `/bo/workshops/${workshopId}/enrollments/${enrollmentId}/attendance`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { workshopId }) => [
        { type: "workshops", id: `${workshopId}-enrollments` },
      ],
    }),

    markAbsence: build.mutation<
      void,
      { workshopId: string; enrollmentId: string }
    >({
      query: ({ workshopId, enrollmentId }) => ({
        url: `/bo/workshops/${workshopId}/enrollments/${enrollmentId}/absence`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { workshopId }) => [
        { type: "workshops", id: `${workshopId}-enrollments` },
        "workshops",
      ],
    }),

    manualDrop: build.mutation<
      void,
      { workshopId: string; enrollmentId: string }
    >({
      query: ({ workshopId, enrollmentId }) => ({
        url: `/bo/workshops/${workshopId}/enrollments/${enrollmentId}/drop`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { workshopId }) => [
        { type: "workshops", id: `${workshopId}-enrollments` },
        "workshops",
      ],
    }),

    recordOfflinePayment: build.mutation<
      void,
      { workshopId: string; enrollmentId: string; amount: number }
    >({
      query: ({ workshopId, enrollmentId, amount }) => ({
        url: `/bo/workshops/${workshopId}/enrollments/${enrollmentId}/offline-payment`,
        method: "POST",
        body: { amount },
      }),
      invalidatesTags: (result, error, { workshopId }) => [
        { type: "workshops", id: `${workshopId}-enrollments` },
        { type: "workshops", id: `${workshopId}-payments` },
      ],
    }),

    // ─── Payments ─────────────────────────────────────────────────

    getWorkshopPayments: build.query<WorkshopPayment[], string>({
      query: (workshopId) => ({
        url: `/bo/workshops/${workshopId}/payments`,
      }),
      transformResponse: (response: any) => {
        return response.success && Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
      },
      providesTags: (result, error, workshopId) => [
        { type: "workshops", id: `${workshopId}-payments` },
      ],
    }),

    recordPayment: build.mutation<
      void,
      {
        workshopId: string;
        enrollmentId: string;
        amount: number;
        source: "MERCADO_PAGO" | "OFFLINE";
        paymentId?: string;
      }
    >({
      query: ({ workshopId, ...body }) => ({
        url: `/bo/workshops/${workshopId}/payment`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { workshopId }) => [
        { type: "workshops", id: `${workshopId}-payments` },
        { type: "workshops", id: `${workshopId}-enrollments` },
      ],
    }),

    // ─── Alerts ───────────────────────────────────────────────────

    getWorkshopAlerts: build.query<WorkshopAlert[], void>({
      query: () => ({
        url: "/bo/workshops/alerts",
      }),
      transformResponse: (response: any) => {
        return response.success && Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
      },
      providesTags: ["workshops"],
    }),
  }),
});

export const {
  useGetWorkshopsQuery,
  useGetWorkshopByIdQuery,
  useCreateWorkshopMutation,
  useUpdateWorkshopMutation,
  useDeleteWorkshopMutation,
  useUpdateWorkshopsSortOrderMutation,
  useGetWorkshopEnrollmentsQuery,
  useMarkAttendanceMutation,
  useMarkAbsenceMutation,
  useManualDropMutation,
  useRecordOfflinePaymentMutation,
  useGetWorkshopPaymentsQuery,
  useRecordPaymentMutation,
  useGetWorkshopAlertsQuery,
} = workshopsApi;
